// Lightweight Web Audio chimes — no asset files, synthesized on the fly.
// Triggered from user gestures (e.g. clicking Claim), so autoplay policies allow it.

let ctx = null

function audioCtx() {
  if (typeof window === 'undefined') return null
  const AC = window.AudioContext || window.webkitAudioContext
  if (!AC) return null
  if (!ctx) ctx = new AC()
  if (ctx.state === 'suspended') ctx.resume()
  return ctx
}

/**
 * A short, sparkly ascending arpeggio for claiming a reward.
 */
export function playClaimChime() {
  try {
    const ac = audioCtx()
    if (!ac) return
    const now = ac.currentTime
    const notes = [523.25, 659.25, 783.99, 1046.5]   // C5 · E5 · G5 · C6
    notes.forEach((freq, i) => {
      const osc = ac.createOscillator()
      const gain = ac.createGain()
      osc.type = 'triangle'
      osc.frequency.value = freq
      const t = now + i * 0.075
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.exponentialRampToValueAtTime(0.16, t + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.38)
      osc.connect(gain)
      gain.connect(ac.destination)
      osc.start(t)
      osc.stop(t + 0.42)
    })
    // A soft shimmer on top of the final note.
    const shimmer = ac.createOscillator()
    const sg = ac.createGain()
    shimmer.type = 'sine'
    shimmer.frequency.setValueAtTime(1568, now + 0.22)   // G6
    const st = now + 0.22
    sg.gain.setValueAtTime(0.0001, st)
    sg.gain.exponentialRampToValueAtTime(0.08, st + 0.03)
    sg.gain.exponentialRampToValueAtTime(0.0001, st + 0.5)
    shimmer.connect(sg)
    sg.connect(ac.destination)
    shimmer.start(st)
    shimmer.stop(st + 0.55)
  } catch (e) { /* sound is best-effort */ }
}

/**
 * The threshold. Not a reward chime — an arrival. A low bowl that swells
 * slowly, a fifth above it that opens a beat later, and a handful of high
 * sparkles scattered across the tail, all detuned in pairs so it shimmers
 * rather than beeps. About three seconds, and it fades rather than stops.
 */
export function playThresholdChime() {
  try {
    const ac = audioCtx()
    if (!ac) return
    const now = ac.currentTime
    const out = ac.createGain()
    out.gain.value = 0.9
    // A short feedback delay stands in for a room.
    const delay = ac.createDelay(1.0)
    delay.delayTime.value = 0.27
    const fb = ac.createGain()
    fb.gain.value = 0.32
    const wet = ac.createGain()
    wet.gain.value = 0.35
    out.connect(ac.destination)
    out.connect(delay)
    delay.connect(fb)
    fb.connect(delay)
    delay.connect(wet)
    wet.connect(ac.destination)

    const voice = (freq, t, { attack, hold, release, level, type = 'sine', detune = 0 }) => {
      const osc = ac.createOscillator()
      const g = ac.createGain()
      osc.type = type
      osc.frequency.value = freq
      osc.detune.value = detune
      g.gain.setValueAtTime(0.0001, t)
      g.gain.exponentialRampToValueAtTime(level, t + attack)
      g.gain.setValueAtTime(level, t + attack + hold)
      g.gain.exponentialRampToValueAtTime(0.0001, t + attack + hold + release)
      osc.connect(g)
      g.connect(out)
      osc.start(t)
      osc.stop(t + attack + hold + release + 0.05)
    }

    // The bowl: a low D, two voices a few cents apart so it beats slowly.
    voice(146.83, now, { attack: 0.9, hold: 0.6, release: 1.8, level: 0.11, detune: -4 })
    voice(146.83, now, { attack: 0.9, hold: 0.6, release: 1.8, level: 0.11, detune: 5 })
    // The fifth above, opening a beat later.
    voice(220.0, now + 0.55, { attack: 0.7, hold: 0.5, release: 1.6, level: 0.07, detune: 3 })
    voice(293.66, now + 0.9, { attack: 0.6, hold: 0.4, release: 1.5, level: 0.05, type: 'triangle' })
    // Sparkles: high, brief, scattered, none of them on the beat.
    const sparkle = [
      [1174.66, 0.35], [1567.98, 0.62], [1975.53, 0.81], [2349.32, 1.05],
      [1567.98, 1.34], [2637.02, 1.58], [1975.53, 1.93], [3135.96, 2.2],
    ]
    for (const [f, dt] of sparkle) {
      voice(f, now + dt, { attack: 0.01, hold: 0.02, release: 0.55, level: 0.035 })
    }
  } catch (e) { /* sound is best-effort */ }
}
